import { defineComponent, inject, ref, watch, onMounted, computed, onUnmounted } from '@vue/composition-api';
import { Card, Empty, Row, Button } from 'element-ui';
import { clone, isValid } from '@formily/shared';
import { useField, useFieldSchema, RecursionField, h } from '@formily/vue';
import { observer } from '@formily/reactive-vue';
import { stylePrefix } from '@formily/element/lib/__builtins__/configs';
import { ArrayBase } from '@formily/element';
import { composeExport } from '@formily/element/lib/__builtins__/shared';
import { List, Toast } from 'vant';
import store from '@/store';

// 模块级注入一次：紧凑空占位样式（两个分支共用）
(function injectEmptyCompactStyles() {
  if (typeof document === 'undefined') return;
  const styleId = `${stylePrefix}-array-cards-empty-compact-styles`;
  if (document.getElementById(styleId)) return;
  const prefix = `${stylePrefix}-array-cards`;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    .${prefix}-empty-container {
      padding: 0 !important;
      min-height: 0 !important;
      margin: 0 !important;
    }
    .${prefix}-empty-container .${prefix}-empty-card,
    .${prefix}-empty-container .el-card {
      min-height: 0 !important;
      margin: 0 auto !important;
      max-width: 600px;
    }
    .${prefix}-empty-container .el-card__header {
      padding: 10px 14px !important;
    }
    .${prefix}-empty-container .el-card__body {
      padding: 8px 12px !important;
    }
    .${prefix}-empty-container .el-empty {
      padding: 8px 0 !important;
    }
    .${prefix}-empty-container .el-empty__image {
      width: 40px !important;
      height: 40px !important;
      margin-bottom: 4px !important;
    }
    .${prefix}-empty-container .el-empty__image img,
    .${prefix}-empty-container .el-empty__image svg {
      width: 100% !important;
      height: 100% !important;
    }
    .${prefix}-empty-container .el-empty__description {
      margin-top: 4px !important;
      font-size: 12px !important;
      line-height: 16px !important;
    }
    .${prefix}-empty-container .el-empty__description p {
      margin: 0 !important;
      font-size: 12px !important;
      line-height: 16px !important;
    }
    .${prefix}-empty-container .el-empty__bottom {
      margin-top: 4px !important;
    }
    /* Addition 按钮紧贴空占位/卡片 */
    .${prefix} .${stylePrefix}-array-base-addition.el-button {
      margin: 4px auto !important;
    }
    .${prefix} > div > .${stylePrefix}-array-base-addition,
    .${prefix} > .${stylePrefix}-array-base-addition {
      margin-top: 4px !important;
    }
  `;
  document.head.appendChild(style);
})();

const isAdditionComponent = (schema) => schema['x-component']?.indexOf('Addition') > -1;

const isIndexComponent = (schema) => schema['x-component']?.indexOf('Index') > -1;

const isRemoveComponent = (schema) => schema['x-component']?.indexOf('Remove') > -1;

const isMoveUpComponent = (schema) => schema['x-component']?.indexOf('MoveUp') > -1;

const isMoveDownComponent = (schema) => schema['x-component']?.indexOf('MoveDown') > -1;

const isOperationComponent = (schema) =>
  isAdditionComponent(schema) ||
  isRemoveComponent(schema) ||
  isMoveDownComponent(schema) ||
  isMoveUpComponent(schema);
const ArrayCardsInner = observer(
  defineComponent({
    name: 'FArrayCards',
    props: ['lazy'],
    setup(props, { attrs }) {
      const fieldRef = useField();
      const field = fieldRef.value;
      const HorizontalVersion = attrs.HorizontalVersion;

      if (!HorizontalVersion) {
        const schemaRef = useFieldSchema();
        const prefixCls = `${stylePrefix}-array-cards`;
        const { getKey, keyMap } = ArrayBase.useKey(schemaRef.value);
        const lazy = props.lazy || attrs.lazy || fieldRef.value.length;
        const dataSource = ref([]);
        const value = computed(() => {
          return Array.isArray(field.value) ? Array.from(field.value) : [];
        });

        // ===== 配置项（由表单设计器 componentProps 下发）=====
        // allCollapseBtn: 是否显示「全部收缩/全部展开」按钮（默认 false）
        // defaultAllCollapsed: 卡片是否默认全部收缩（默认 false 即默认全部展开）
        const allCollapseBtn = !!attrs.allCollapseBtn;
        const defaultAllCollapsed = !!attrs.defaultAllCollapsed;

        // ===== 纵向卡片收缩/展开状态 =====
        // 采用「全局模式 + 单卡覆盖」双层结构，而不是按 key 遍历构建完整 map：
        // 1) collapseMode: 当前全局收缩态（true=全部收缩）。默认收缩、全部收缩/展开
        //    直接切换这个标志即可，完全不依赖 key 计算——无论卡片数据何时加载、
        //    key 形态如何（对象/原始值数组）、渲染数据源走懒加载 dataSource 还是
        //    直读 field.value，默认收缩都一律生效（修复无操作按钮表格下
        //    「默认全部收缩」不生效的问题）。
        // 2) overrideMap: 单卡点击标题行的覆盖状态，key 为卡片唯一标识，
        //    删除/上移/下移后残留的 key 只是无害的脏数据。
        const collapseMode = ref(false);
        const overrideMap = ref({});
        // 注意：不能通过 value computed 取卡片数量——它的 getter 只读 formily 的
        // field.value，不含任何 Vue 响应式依赖，Vue 2 会把首次求值结果永久缓存，
        // 字段值后续变化（如新增行）不会使其失效。这里用普通函数直接读 field.value。
        const getCardsCount = () => (Array.isArray(field.value) ? field.value.length : 0);
        // 取卡片唯一标识，getKey 无返回时回退到下标，保证始终可用
        const getCardKey = (item, index) => String(getKey(item, index) ?? index);
        // 单张卡片当前是否收缩：单卡覆盖优先，否则跟随全局模式
        const isCardCollapsed = (key) => {
          if (overrideMap.value[key] !== undefined) return overrideMap.value[key];
          return collapseMode.value;
        };
        // 是否全部处于收缩态：全局收缩且没有任何卡片被单卡覆盖为展开
        // （渲染期间调用时读取 collapseMode/overrideMap 可触发重渲）
        const isAllCollapsed = () => {
          if (!collapseMode.value) return false;
          return Object.values(overrideMap.value).every((v) => v === true);
        };
        // 切换单张卡片收缩/展开
        const toggleCardCollapse = (key) => {
          const map = { ...overrideMap.value };
          map[key] = !isCardCollapsed(key);
          overrideMap.value = map;
        };
        // 全部收缩（重置单卡覆盖，切换全局模式，不遍历数据、不计算 key）
        const collapseAllCards = () => {
          overrideMap.value = {};
          collapseMode.value = true;
        };
        // 全部展开
        const expandAllCards = () => {
          overrideMap.value = {};
          collapseMode.value = false;
        };
        // 全部收缩/全部展开 切换
        const toggleAllCards = () => {
          if (isAllCollapsed()) expandAllCards();
          else collapseAllCards();
        };

        // 默认收缩/展开：defaultAllCollapsed 开启时，数据首次出现（含异步加载）时全部收缩。
        // 注意：不能用 watch 监听 field.value 的长度——field.value 是 formily 自维护的数据，
        // 不是 Vue 响应式依赖，watch 除 immediate 首次执行外不会再触发；表单数据异步加载时
        // 首次执行拿到的还是 0，watch 后续也不会再跑，导致默认收缩失效。
        // 因此改为在渲染函数里检测：首次渲染出卡片数据的那一刻执行一次全部收缩。
        // collapseAllCards 只切换全局模式标志，不依赖 key，任何渲染链路下都生效。
        let defaultCollapsedApplied = false;
        const applyDefaultCollapsedIfNeeded = () => {
          if (defaultCollapsedApplied) return;
          if (!allCollapseBtn || !defaultAllCollapsed) return;
          const list = Array.isArray(field.value) ? field.value : [];
          if (!list.length) return;
          defaultCollapsedApplied = true;
          collapseAllCards();
        };

        watch(
          () => (Array.isArray(field.value) ? field.value.length : 0),
          (newLen, oldLen) => {
            if (newLen > 0 && !oldLen) applyDefaultCollapsedIfNeeded();
          },
          { immediate: true }
        );

        watch(() => attrs.value, (newValue, oldValue) => {
          if (lazy && newValue?.length && !oldValue?.length) addCard()
        }, {
          deep: true, immediate: true
        })

        // 读取七彩石配置，决定是否启用 cardRefreshKeys（带异常兜底）
        // 只有 iOS 设备才启用此功能




        const toast = ref(null);
        const finished = ref(false);
        const loading = ref(false);
        const addCard = () => {

          loading.value = true;
          dataSource.value.push(
            ...(Array.isArray(field.value) ? Array.from(field.value) : []).slice(
              dataSource.value.length,
              dataSource.value.length + lazy
            )
          );

        };
        const handleScroll = () => {
          let scrollTop = document.documentElement.scrollTop || document.body.scrollTop; // 滚动条距离顶部的距离
          let windowHeight = document.documentElement.clientHeight || document.body.clientHeight; // 可视区的高度
          let scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight; //dom元素的高度，包含溢出不可见的内容
          if (scrollHeight - 300 <= scrollTop + windowHeight) {
            if (finished.value || dataSource.value.length >= value.value.length) {
              finished.value = true;
              toast.value?.clear();
              toast.value = null;
              return;
            }
            addCard();
          }
        };
        if (lazy) {
          document.addEventListener('scroll', handleScroll);
          onUnmounted(() => {
            document.removeEventListener('scroll', handleScroll);
          })
        }

        return () => {
          const schema = schemaRef.value;
          const value = Array.isArray(field.value) ? Array.from(field.value) : [];
          if (!schema) throw new Error('can not found schema object');
          // 数据首次渲染出来时应用默认收缩（覆盖异步加载场景，见 applyDefaultCollapsedIfNeeded 注释）
          applyDefaultCollapsedIfNeeded();
          const renderItem = (data) =>
            data?.map((item, index) => {
              const items = Array.isArray(schema.items)
                ? schema.items[index] || schema.items[0]
                : schema.items;

              // 兜底：items 无效时跳过该卡片渲染
              if (!items || !items.properties) {
                console.warn('[ArrayCards] renderItem: items 无效，跳过渲染', { index });
                return null;
              }

              // 纵向卡片收缩/展开（纵向模式默认生效，收缩时仅展示标题行）
              // 按卡片 key 记录状态，删除/上移/下移后不会错位
              const cardKey = getCardKey(item, index);
              const isCollapsed = allCollapseBtn && isCardCollapsed(cardKey);
              // 点击标题行切换收缩/展开；点击操作按钮（删除/上移/下移）不触发
              const handleHeaderClick = (e) => {
                if (!allCollapseBtn) return;
                if (e.target && e.target.closest && e.target.closest('button')) return;
                toggleCardCollapse(cardKey);
              };

              const title = h(
                'span',
                {},
                {
                  default: () => [
                    h(
                      RecursionField,
                      {
                        props: {
                          schema: items,
                          name: index,
                          filterProperties: (schema) => {
                            if (!isIndexComponent(schema)) return false;
                            return true;
                          },
                          onlyRenderProperties: true,
                        },
                      },
                      {}
                    ),
                    (attrs.titleDesc && item[attrs.titleDesc]) || attrs.title || field.title,
                  ],
                }
              );
              const extra = h(
                'span',
                {},
                {
                  default: () => [
                    h(
                      RecursionField,
                      {
                        props: {
                          schema: items,
                          name: index,
                          filterProperties: (schema) => {
                            if (!isOperationComponent(schema)) return false;
                            return true;
                          },
                          onlyRenderProperties: true,
                        },
                      },
                      {}
                    ),
                    attrs.extra,
                    // 全部收缩/全部展开按钮：受 allCollapseBtn 开关控制，
                    // 仅当卡片数多于 1 个时，出现在第一张卡片的标题栏（extra）区域
                    index === 0 && allCollapseBtn && data.length > 1
                      ? h(
                          'span',
                          {
                            class: `${prefixCls}-collapse-toolbar-btn`,
                            style: {
                              marginLeft: '12px',
                              fontSize: '13px',
                              color: '#1890ff',
                              cursor: 'pointer',
                              userSelect: 'none',
                            },
                            on: {
                              click: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                toggleAllCards();
                              },
                            },
                          },
                          {
                            default: () => (isAllCollapsed() ? '全部展开' : '全部收缩'),
                          }
                        )
                      : null,
                    // 收缩/展开指示箭头（@formily/vue 的 h 必须传第三个参数，两参调用会抛错）
                    // 收缩/展开指示箭头（受 allCollapseBtn 开关控制；
                    // @formily/vue 的 h 必须传第三个参数，两参调用会抛错）
                    allCollapseBtn
                      ? h(
                          'i',
                          {
                            class: isCollapsed ? 'el-icon-arrow-right' : 'el-icon-arrow-down',
                            style: {
                              marginLeft: '8px',
                              color: '#909399',
                              fontSize: '14px',
                            },
                          },
                          {}
                        )
                      : null,
                  ],
                }
              );
              // 根据七彩石配置决定是否传递 key
              const contentData = {
                    props: {
                      schema: items,
                      name: index,
                      filterProperties: (schema) => {
                        if (isIndexComponent(schema)) return false;
                        if (isOperationComponent(schema)) return false;
                        return true;
                      },
                    },
                  };

              const content = h(
                RecursionField,
                contentData,
                {}
              );

              return h(
                ArrayBase.Item,
                {
                  key: getKey(item, index),
                  value: loading,
                  finished,
                  finishedText: '没有更多了',
                  props: {
                    index,
                    record: item,
                  },
                },
                {
                  default: () =>
                    h(
                      Card,
                      {
                        class: [
                          `${prefixCls}-item mobileCardHorizontalVersion`,
                          isCollapsed ? `${prefixCls}-item-collapsed` : '',
                        ],
                        attrs: {
                          shadow: 'never',
                          ...attrs,
                          // 收缩时隐藏卡片内容区，仅展示标题行（字段仍渲染，保留表单值与校验状态）
                          'body-style': isCollapsed ? { display: 'none' } : undefined,
                        },
                      },
                      {
                        default: () => [content],
                        header: () =>
                          h(
                            Row,
                            {
                              props: {
                                type: 'flex',
                                justify: 'space-between',
                              },
                            nativeOn: { click: handleHeaderClick },
                            style: { cursor: allCollapseBtn ? 'pointer' : 'default' },
                            },
                            {
                              default: () => [title, extra],
                            }
                          ),
                      }
                    ),
                }
              );
            });

          const renderAddition = () =>
            schema.reduceProperties((addition, schema) => {
              if (isAdditionComponent(schema)) {
                return h(
                  RecursionField,
                  {
                    props: {
                      schema,
                      name: 'addition',
                    },
                  },
                  {}
                );
              }
              return addition;
            }, null);
          const renderEmpty = () => {
            if (!renderAddition() && lazy ? (dataSource.value?.length || (!dataSource.value?.length && value?.length)) : value?.length) return;
            return h(
              'div',
              {
                class: `${prefixCls}-empty-container`,
                style: {
                  textAlign: 'center',
                  padding: '0',
                  minHeight: '0',
                },
              },
              {
                default: () => h(
                  Card,
                  {
                    class: [`${prefixCls}-item`, `${prefixCls}-empty-card`],
                    attrs: {
                      shadow: 'never',
                      ...attrs,
                      header: attrs.title || field.title,
                    },
                    style: {
                      maxWidth: '600px',
                      margin: '0 auto',
                      minHeight: '0',
                    },
                  },
                  {
                    default: () => h(Empty, { props: { description: '暂无数据', imageSize: 40 } }, {}),
                  }
                ),
              }
            );
          };

          return h(
            !renderAddition() && lazy ? ArrayBase : 'div',
            {
              class: [prefixCls, 'mobileCard'],
            },
            {
              default: () =>
                h(
                  !renderAddition() && lazy ? List : ArrayBase,
                  {
                    attrs: {
                      offset: 30,
                      loading: loading.value,
                      finished: finished.value,
                      finishedText: '没有更多了',
                    },
                    props: {
                      keyMap,
                    },
                    on: {
                    },
                  },
                  {
                    default: () => [
                      renderEmpty(),
                      renderItem(!renderAddition() && lazy ? dataSource.value : value),
                      renderAddition(),
                    ],
                  }
                ),
            }
          );
        };
      }


      const schemaRef = useFieldSchema();
      const prefixCls = `${stylePrefix}-array-cards`;
      const { getKey, keyMap } = ArrayBase.useKey(schemaRef.value);
      const lazy = props.lazy || attrs.lazy || fieldRef.value.length;
      const convertCollapseBtn = attrs.convertCollapseBtn;
      const dataSource = ref([]);
      const value = computed(() => {
        return Array.isArray(field.value) ? Array.from(field.value) : [];
      });

      // 横向轮播相关状态
      const currentIndex = ref(0);
      const cardContainerRef = ref(null);
      // 记录最后点击的方向：'prev' | 'next' | null
      const lastClickDirection = ref(null);

      // 折叠状态管理 - 统一管理所有卡片的展开/折叠状态
      const isAllExpanded = ref(false);

      watch(() => attrs.value, (newValue, oldValue) => {
        if (lazy && newValue?.length && !oldValue?.length) addCard()
      }, {
        deep: true, immediate: true
      })


      
      const toast = ref(null);
      const finished = ref(false);
      const loading = ref(false);
      const addCard = () => {
        loading.value = true;

        dataSource.value.push(
          ...(Array.isArray(field.value) ? Array.from(field.value) : []).slice(
            dataSource.value.length,
            dataSource.value.length + lazy
          )
        );
      };
      const handleScroll = () => {
        let scrollTop = document.documentElement.scrollTop || document.body.scrollTop; // 滚动条距离顶部的距离
        let windowHeight = document.documentElement.clientHeight || document.body.clientHeight; // 可视区的高度
        let scrollHeight = document.documentElement.scrollHeight || document.body.scrollHeight; //dom元素的高度，包含溢出不可见的内容
        if (scrollHeight - 300 <= scrollTop + windowHeight) {
          // console.log(dataSource.value.length, value.value.length);
          if (finished.value || dataSource.value.length >= value.value.length) {
            finished.value = true;
            toast.value?.clear();
            toast.value = null;
            return;
          }
          addCard();
        }
      };
      if (lazy) {
        document.addEventListener('scroll', handleScroll);
        onUnmounted(() => {
          document.removeEventListener('scroll', handleScroll);
        })
      }

      // 轮播导航方法
      const goToPrevCard = () => {
        const actualValue = Array.isArray(field.value) ? Array.from(field.value) : [];
        const dataLength = lazy ? dataSource.value.length : actualValue.length;
        if (dataLength > 0 && currentIndex.value > 0) {
          currentIndex.value = currentIndex.value - 1;
          lastClickDirection.value = 'prev';
        }
      };

      const goToNextCard = () => {
        const actualValue = Array.isArray(field.value) ? Array.from(field.value) : [];
        const dataLength = lazy ? dataSource.value.length : actualValue.length;
        if (dataLength > 0 && currentIndex.value < dataLength - 1) {
          currentIndex.value = currentIndex.value + 1;
          lastClickDirection.value = 'next';
        }
      };

      // 监听数据变化，处理删除情况
      watch(() => {
        const actualValue = Array.isArray(field.value) ? Array.from(field.value) : [];
        return lazy ? dataSource.value.length : actualValue.length;
      }, (newLength, oldLength) => {
        if (newLength === 0) {
          currentIndex.value = 0;
        } else if (currentIndex.value >= newLength) {
          // 删除后索引越界，跳转到最后一张
          currentIndex.value = newLength - 1;
        }
      });

      return () => {
        const schema = schemaRef.value;

        // 获取实际数据
        const actualValue = Array.isArray(field.value) ? Array.from(field.value) : [];
        // console.log('Render function - actualValue:', actualValue, 'actualValue.length:', actualValue.length, 'lazy:', lazy);

        if (!schema) throw new Error('can not found schema object');

        // 渲染单个卡片
        const renderSingleCard = (item, index) => {

          const items = Array.isArray(schema.items)
            ? schema.items[index] || schema.items[0]
            : schema.items;
          // console.log('item==>',item,schema,items.length);

          // 兜底：items 无效时返回空节点
          if (!items || !items.properties) {
            // console.warn('[ArrayCards-Horizontal] renderSingleCard: items 无效，返回空卡片', { index });
            return h('div', {}, { default: () => [] });
          }

          // 为每个卡片创建唯一的 key
          const cardKey = `card-${index}`;

          // 获取全局展开状态
          const isExpanded = isAllExpanded.value;

          // 切换展开/折叠状态 - 统一控制所有卡片
          const toggleExpand = () => {
            isAllExpanded.value = !isAllExpanded.value;
          };

          const title = h(
            'span',
            {},
            {
              default: () => [
                h(
                  RecursionField,
                  {
                    props: {
                      schema: items,
                      name: index,
                      filterProperties: (schema) => {
                        if (!isIndexComponent(schema)) return false;
                        return true;
                      },
                      onlyRenderProperties: true,
                    },
                  },
                  {}
                ),
                (attrs.titleDesc && item[attrs.titleDesc]) || attrs.title || field.title,
              ],
            }
          );

          const extra = h(
            'span',
            {
              style: {
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: '32px',
              },
            },
            {
              default: () => [
                h(
                  RecursionField,
                  {
                    props: {
                      schema: items,
                      name: index,
                      filterProperties: (schema) => {
                        if (!isOperationComponent(schema)) return false;
                        return true;
                      },
                      onlyRenderProperties: true,
                    },
                  },
                  {}
                ),
                attrs.extra,
              ],
            }
          );

          // 根据七彩石配置决定是否传递 key
          const contentData = {
                props: {
                  schema: items,
                  name: index,
                  filterProperties: (schema) => {
                    if (isIndexComponent(schema)) return false;
                    if (isOperationComponent(schema)) return false;
                    return true;
                  },
                },
              };

          const content = h(
            RecursionField,
            contentData,
            {}
          );

          // 内容容器（带折叠效果）- convertCollapseBtn 开启时限制高度为 220px
          const contentWrapper = h(
            'div',
            {
              class: `${prefixCls}-content-wrapper`,
              style: {
                maxHeight: convertCollapseBtn ? (isExpanded ? 'none' : '220px') : 'none',
                overflow: convertCollapseBtn && !isExpanded ? 'hidden' : 'visible',
                transition: 'max-height 0.3s ease',
                position: 'relative',
              },
            },
            {
              default: () => [content],
            }
          );

          // 展开/折叠按钮 - convertCollapseBtn 开启时始终显示
          const expandButton = convertCollapseBtn ? h(
            'div',
            {
              class: `${prefixCls}-expand-btn`,
              style: {
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '10px 16px',
                backgroundColor: '#fff',
                borderTop: '1px solid #f0f0f0',
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                position: 'relative',
              },
              on: {
                click: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpand();
                },
              },
            },
            {
              default: () => [
                // 展开/折叠按钮
                h(
                  'span',
                  {
                    style: {
                      fontSize: '14px',
                      color: '#1890ff',
                      transition: 'color 0.3s ease',
                    },
                  },
                  {
                    default: () => isExpanded ? '收起' : '展开',
                  }
                ),
              ],
            }
          ) : null;

          return h(
            ArrayBase.Item,
            {
              key: getKey(item, index),
              props: {
                index,
                record: item,
              },
            },
            {
              default: () => {
                return h(
                  Card,
                  {
                    class: [`${prefixCls}-item`, `${prefixCls}-item-${cardKey}`],
                    attrs: {
                      shadow: 'never',
                      ...attrs,
                    },
                    style: {
                      width: '100%',
                      minHeight: '200px',
                      overflow: 'visible',
                    },
                  },
                  {
                    default: () => {
                      const cardContent = [contentWrapper];
                      if (expandButton) {
                        cardContent.push(expandButton);
                      }
                      return cardContent;
                    },
                    header: () =>
                      h(
                        Row,
                        {
                          props: {
                            type: 'flex',
                            justify: 'space-between',
                          },
                        },
                        {
                          default: () => [title, extra],
                        }
                      ),
                  }
                )
              },
            }
          );
        };

        // 渲染轮播容器
        const renderCarousel = (data) => {
          if (!data || data.length === 0) return null;

          // 直接使用 currentIndex.value 以保持响应式
          let idx = currentIndex.value;

          // 确保索引在有效范围内
          if (idx >= data.length) {
            idx = data.length - 1;
            currentIndex.value = idx;
          } else if (idx < 0) {
            idx = 0;
            currentIndex.value = 0;
          }

          // 方案A：所有卡片都渲染（保证每一行的字段都挂载进 Formily 表单），
          // 仅用 display 控制当前显示哪一张，避免未渲染行在提交时数据丢失
          return h(
            'div',
            {
              class: `${prefixCls}-carousel-container mobileCard`,
              style: {
                position: 'relative',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0',
              },
            },
            {
              default: () =>
                data.map((item, i) => {
                  // 找到该卡片在完整数据中的真实索引
                  const found = lazy ? actualValue.indexOf(item) : i;
                  const realIndex = found >= 0 ? found : i;
                  const isActive = i === idx;

                  return h(
                    'div',
                    {
                      key: getKey(item, realIndex),
                      class: [
                        `${prefixCls}-card-wrapper`,
                        isActive
                          ? `${prefixCls}-card-wrapper-active`
                          : `${prefixCls}-card-wrapper-hidden`,
                      ],
                      style: {
                        // 卡片内容区域（无左右padding，让内容占满宽度）
                        width: '100%',
                        maxWidth: '100%',
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        zIndex: 1,
                        padding: '0',
                        display: isActive ? 'block' : 'none',
                      },
                      ...(isActive ? { ref: cardContainerRef } : {}),
                    },
                    {
                      default: () => [renderSingleCard(item, realIndex)],
                    }
                  );
                }),
            }
          );
        };

        // 渲染分页导航控件（独立出来，方便和添加按钮合并到同一行）
        const renderPagination = (data) => {
          if (!data || data.length <= 1) return null;

          let idx = currentIndex.value;
          if (idx >= data.length) idx = data.length - 1;
          if (idx < 0) idx = 0;

          // 判断哪个按钮高亮：根据最后点击的方向
          const isPrevActive = lastClickDirection.value === 'prev';
          const isNextActive = lastClickDirection.value === 'next';

          return h(
            'div',
            {
              class: `${prefixCls}-pagination-bar`,
              style: {
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '16px',
                padding: '8px 16px',
                backgroundColor: '#f8fafc',
                borderRadius: '24px',
                boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
              },
            },
            {
              default: () => [
                // 左箭头按钮
                h(
                  'div',
                  {
                    class: `${prefixCls}-arrow-btn ${prefixCls}-arrow-prev`,
                    style: {
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: isPrevActive ? '#79bbff' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      userSelect: 'none',
                      border: isPrevActive ? '1px solid #79bbff' : '1px solid #e4e7ed',
                      flexShrink: 0,
                      fontSize: '12px',
                      fontWeight: '600',
                      color: isPrevActive ? '#fff' : '#64748b',
                      lineHeight: '32px',
                      textAlign: 'center',
                    },
                    on: {
                      click: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        goToPrevCard();
                      },
                    },
                  },
                  {
                    default: () => '<',
                  }
                ),

                // 页码指示器
                h(
                  'div',
                  {
                    class: `${prefixCls}-page-info`,
                    style: {
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '14px',
                      color: '#64748b',
                      fontWeight: '500',
                      minWidth: '60px',
                      justifyContent: 'center',
                    },
                  },
                  {
                    default: () => [
                      h('span', { style: { color: '#409eff', fontWeight: '600' } }, { default: () => idx + 1 }),
                      h('span', {}, { default: () => '/' }),
                      h('span', {}, { default: () => data.length }),
                    ],
                  }
                ),

                // 右箭头按钮
                h(
                  'div',
                  {
                    class: `${prefixCls}-arrow-btn ${prefixCls}-arrow-next`,
                    style: {
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: isNextActive ? '#79bbff' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      userSelect: 'none',
                      border: isNextActive ? '1px solid #79bbff' : '1px solid #e4e7ed',
                      flexShrink: 0,
                      fontSize: '12px',
                      fontWeight: '600',
                      color: isNextActive ? '#fff' : '#64748b',
                      lineHeight: '32px',
                      textAlign: 'center',
                    },
                    on: {
                      click: (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        goToNextCard();
                      },
                    },
                  },
                  {
                    default: () => '>',
                  }
                ),
              ],
            }
          );
        };

        // const cardItems = computed(() => );
        const renderAddition = () =>
          schema.reduceProperties((addition, schema) => {

            if (isAdditionComponent(schema)) {
              return h(
                RecursionField,
                {
                  props: {
                    schema,
                    name: 'addition',
                  },
                },
                {}
              );
            }
            return addition;
          }, null);
        const renderEmpty = () => {
          const shouldRender = !renderAddition() && lazy
            ? (dataSource.value?.length || (!dataSource.value?.length && actualValue?.length))
            : actualValue?.length;

          if (shouldRender) return;

          return h(
            'div',
            {
              class: `${prefixCls}-empty-container`,
              style: {
                textAlign: 'center',
                padding: '0',
                minHeight: '0',
              },
            },
            {
              default: () => h(
                Card,
                {
                  class: [`${prefixCls}-item`, `${prefixCls}-empty-card`],
                  attrs: {
                    shadow: 'never',
                    ...attrs,
                    header: attrs.title || field.title,
                  },
                  style: {
                    maxWidth: '600px',
                    margin: '0 auto',
                    minHeight: '0',
                  },
                },
                {
                  default: () => h(Empty, { props: { description: '暂无数据', imageSize: 40 } }, {}),
                }
              ),
            }
          );
        };

        // 添加样式到页面
        const addStyles = () => {
          const styleId = `${prefixCls}-horizontal-styles`;
          if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.textContent = `
              .${prefixCls}-horizontal {
                position: relative;
                overflow: visible;
              }
              
              .${prefixCls}-carousel-container {
                user-select: none;
                overflow: visible;
              }
              
              /* 卡片包装器 */
              .${prefixCls}-card-wrapper .${prefixCls}-item {
                margin-bottom: 0 !important;
              }
              
              /* 底部分页控制器 */
              .${prefixCls}-pagination-bar {
                user-select: none;
              }
              
              /* 左箭头按钮悬停效果 */
              .${prefixCls}-arrow-prev:hover {
                background-color: #1677ff !important;
                border-color: #1677ff !important;
                color: #fff !important;
                transform: scale(1.05);
                box-shadow: 0 2px 8px rgba(22, 119, 255, 0.3);
              }
              
              /* 右箭头按钮悬停效果 */
              .${prefixCls}-arrow-next:hover {
                background-color: #4096ff !important;
                border-color: #4096ff !important;
                transform: scale(1.05);
                box-shadow: 0 2px 8px rgba(22, 119, 255, 0.4);
              }
              
              /* 箭头按钮点击效果 */
              .${prefixCls}-arrow-btn:active {
                transform: scale(0.95) !important;
              }
              
              .${prefixCls}-arrow-prev:active {
                background-color: #0958d9 !important;
                border-color: #0958d9 !important;
              }
              
              .${prefixCls}-arrow-next:active {
                background-color: #0958d9 !important;
                border-color: #0958d9 !important;
              }
              
              /* 内容折叠包装器 */
              .${prefixCls}-content-wrapper {
                line-height: 1.5;
              }
              
              /* 展开/折叠按钮悬停效果 */
              .${prefixCls}-expand-btn:hover {
                background-color: #f5f5f5 !important;
              }
              
              .${prefixCls}-expand-btn:hover > span {
                color: #1890ff !important;
              }
              
              .${prefixCls}-expand-btn:active {
                background-color: #e6e6e6 !important;
              }

              /* 底部操作栏：分页+添加按钮对齐 */
              .${prefixCls}-bottom-action-bar {
                display: flex !important;
                align-items: center !important;
                justify-content: center !important;
              }
              .${prefixCls}-bottom-action-bar .${stylePrefix}-array-base-addition,
              .${prefixCls}-bottom-action-bar .${stylePrefix}-array-base-addition.el-button {
                margin: 0 !important;
                flex-shrink: 0;
              }
              .${prefixCls}-bottom-action-bar .${prefixCls}-pagination-bar {
                margin: 0 !important;
                margin-bottom: 6px !important
              }

              /* 移动端适配 */
              @media (max-width: 768px) {
                .${prefixCls}-pagination-bar {
                  gap: 12px !important;
                  padding: 6px 12px !important;
                }
                
                .${prefixCls}-arrow-btn {
                  width: 28px !important;
                  height: 28px !important;
                  font-size: 16px !important;
                }
                
                .${prefixCls}-page-info {
                  font-size: 13px !important;
                  min-width: 50px !important;
                }
              }
              
              /* 小屏幕优化 */
              @media (max-width: 480px) {
                .${prefixCls}-pagination-bar {
                  gap: 8px !important;
                  padding: 4px 10px !important;
                  margin-top: 8px !important;
                }
                
                .${prefixCls}-arrow-btn {
                  width: 26px !important;
                  height: 26px !important;
                  font-size: 14px !important;
                }
                
                .${prefixCls}-page-info {
                  font-size: 12px !important;
                  min-width: 45px !important;
                }
              }
            `;
            document.head.appendChild(style);
          }
        };

        // 在组件挂载时添加样式
        onMounted(() => {
          addStyles();
        });

        return h(
          ArrayBase,
          {
            class: [prefixCls, `${prefixCls}-horizontal`, 'mobileCard'],
            props: {
              keyMap,
            },
            style: {
              // padding: '20px 0 !important',
              minHeight: '0',
            },
            on: {
              add: (index) => {
                // 当添加新卡片时，跳转到新卡片
                const actualValue = Array.isArray(field.value) ? Array.from(field.value) : [];
                const dataLength = lazy ? dataSource.value.length : actualValue.length;
                currentIndex.value = dataLength - 1;
              },
            },
          },
          {
            default: () => {
              // 使用实际数据而不是 computed value
              const dataToRender = !renderAddition() && lazy ? dataSource.value : actualValue;
              // console.log('Main render - dataToRender:', dataToRender, 'lazy:', lazy, 'dataSource:', dataSource.value.length, 'actualValue:', actualValue.length);

              return [
                renderEmpty(),
                renderCarousel(dataToRender),
                // 底部操作栏：分页导航 + 添加按钮
                (() => {
                  const addition = renderAddition();
                  const pagination = renderPagination(dataToRender);
                  
                  // 有添加按钮时：分页导航和添加按钮放同一行
                  if (addition && pagination) {
                    return h(
                      'div',
                      {
                        class: `${prefixCls}-bottom-action-bar`,
                        style: {
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '16px',
                          marginTop: '12px',
                        },
                      },
                      {
                        default: () => [pagination, addition],
                      }
                    );
                  }
                  
                  // 只有添加按钮，无分页
                  if (addition) {
                    return h(
                      'div',
                      {
                        style: {
                          textAlign: 'center',
                          marginTop: '8px',
                        },
                      },
                      {
                        default: () => [addition],
                      }
                    );
                  }
                  
                  // 只有分页导航，无添加按钮 - 居中显示
                  if (pagination) {
                    return h(
                      'div',
                      {
                        style: {
                          display: 'flex',
                          justifyContent: 'center',
                          marginTop: '12px',
                        },
                      },
                      {
                        default: () => [pagination],
                      }
                    );
                  }
                  
                  return null;
                })(),
              ];
            },
          }
        );
      };
    },
  })
);

const ArrayBaseSymbol = Symbol('ArrayBaseContext');
const ItemSymbol = Symbol('ItemContext');

const useArray = () => {
  return inject(ArrayBaseSymbol, null);
};

const getDefaultValue = (defaultValue, schema) => {
  if (isValid(defaultValue)) return clone(defaultValue);
  if (Array.isArray(schema?.items)) return getDefaultValue(defaultValue, schema.items[0]);
  return {};
};

const ArrayBaseAddition = defineComponent({
  name: 'ArrayBaseAddition',
  props: ['title', 'method', 'defaultValue', 'backgroundColor', 'borderColor', 'borderWidth', 'fontColor'],
  setup(props, { listeners }) {
    const self = useField();
    const array = ArrayBase.useArray();
    const prefixCls = `${stylePrefix}-array-base`;
    return () => {
      if (array?.field.value.pattern !== 'editable') return null;

      // 构建自定义样式
      const customStyle = {};
      if (props.backgroundColor) {
        const bg = props.backgroundColor.startsWith('#') ? props.backgroundColor : `#${props.backgroundColor}`;
        customStyle.backgroundColor = bg;
      }
      if (props.borderColor) {
        const bc = props.borderColor.startsWith('#') ? props.borderColor : `#${props.borderColor}`;
        customStyle.borderColor = bc;
      }
      if (props.borderWidth) {
        customStyle.borderWidth = props.borderWidth;
        customStyle.borderStyle = 'solid';
      }
      if (props.fontColor) {
        const fc = props.fontColor.startsWith('#') ? props.fontColor : `#${props.fontColor}`;
        customStyle.color = fc;
      }

      return h(
        Button,
        {
          class: `${prefixCls}-addition`,
          attrs: {
            type: 'ghost',
            icon: 'qax-icon-Alone-Plus',
            ...props,
          },
          style: customStyle,
          on: {
            ...listeners,
            click: (e) => {
              if (array.props?.disabled) return;
              const defaultValue = getDefaultValue(props.defaultValue, array?.schema.value);
              if (props.method === 'unshift') {
                array?.field?.value.unshift(defaultValue);
                array.listeners?.add?.(0);
              } else {
                array?.field?.value.push(defaultValue);
                array.listeners?.add?.(array?.field?.value?.value?.length - 1);
              }
              if (listeners.click) {
                listeners.click(e);
              }
            },
          },
        },
        {
          default: () => [self.value.title || props.title],
        }
      );
    };
  },
});
// 自定义 Remove 组件，支持 x-visible/x-hidden 控制显示隐藏
const ArrayBaseRemove = defineComponent({
  name: 'ArrayBaseRemove',
  props: ['title', 'method', 'defaultValue'],
  setup(props, { listeners, attrs }) {
    const fieldRef = useField();
    const array = ArrayBase.useArray();
    const indexRef = ArrayBase.useIndex();
    const prefixCls = `${stylePrefix}-array-base`;
    return () => {
      const field = fieldRef.value;
      // 检查 field.visible（由 x-visible 控制）
      if (field?.visible === false) return null;
      // 检查 field.hidden（由 x-hidden 控制）
      if (field?.hidden === true) return null;
      // 检查 pattern 是否为 editable
      if (array?.field?.value?.pattern !== 'editable') return null;
      // 检查 disabled
      if (array?.props?.disabled) return null;

      return h(
        Button,
        {
          class: `${prefixCls}-remove`,
          attrs: {
            type: 'text',
            icon: 'el-icon-delete',
            ...props,
            ...attrs,
          },
          style: {
            color: '#409EFF',
            padding: '2px 4px',
            border: 'none',
            background: 'none',
          },
          on: {
            ...listeners,
            click: (e) => {
              e.preventDefault();
              e.stopPropagation();
              if (array?.field?.value) {
                const currentIndex = indexRef.value;
                if (currentIndex !== undefined && currentIndex !== null) {
                  const arr = array.field.value.value;
                  if (Array.isArray(arr)) {
                    arr.splice(currentIndex, 1);
                  }
                }
              }
              if (listeners?.click) {
                listeners.click(e);
              }
            },
          },
        },
        {
          default: () => [],
        }
      );
    };
  },
});

export const MobileArrayCards = composeExport(ArrayCardsInner, {
  Index: ArrayBase.Index,
  SortHandle: ArrayBase.SortHandle,
  Addition: ArrayBaseAddition,
  Remove: ArrayBaseRemove,
  MoveDown: ArrayBase.MoveDown,
  MoveUp: ArrayBase.MoveUp,
  useArray: ArrayBase.useArray,
  useIndex: ArrayBase.useIndex,
  useRecord: ArrayBase.useRecord,
});
export default MobileArrayCards;